import { useState } from 'react';

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex justify-between items-center text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <span className="text-lg md:text-xl font-semibold text-gray-900 pr-8">
          {question}
        </span>
        <span className="text-2xl text-amber-600 flex-shrink-0">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      
      {isOpen && (
        <div className="pb-6 text-lg text-gray-700 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQSection() {
  const faqs = [
    {
      question: "What's the difference between the guide and the session?",
      answer: "The guide gives you the framework and examples you can apply immediately. The session is for when you want to go deeper. We'll clarify your specific signal, identify aligned opportunities, and create a personalized 30-day action plan together."
    },
    {
      question: "Who is this for?",
      answer: "People who are fun, consciousness-forward, and building something meaningful. If you're tired of transactional networking and want partnerships that feel generous and aligned, this is for you."
    },
    {
      question: "I downloaded the guide. What happens next?",
      answer: "You'll get the PDF immediately. Read it. Use the reflection prompts. Trust what resonates. If you want to go deeper, book the session anytime."
    },
    {
      question: "What if I'm not sure if we're aligned?",
      answer: "If you're asking that question, you probably already know. Trust your gut. That's ICK vs. STICK in action."
    },
    {
      question: "Will you introduce me to potential partners or help me close deals?",
      answer: "The initial session is about clarity and strategy, not introductions or deal execution. However, resources and connections often emerge organically through the conversation."
    },
    {
      question: "How do I hire you for consulting or advisory work?",
      answer: "Fill out the consulting application above. I'll review it and reach out if it feels like an aligned fit. If you haven't done the Partnership Clarity Session yet, I highly recommend starting there."
    },
    {
      question: "What if I want to hire you to manage partnerships for me?",
      answer: "I don't run partnerships or operate as an agency. I advise, strategize, and make connections as guided, but the execution is yours (or your team's)."
    },
    {
      question: "Who do you work with for ongoing consulting?",
      answer: "I work best with people in health, wellness, personal development, or conscious entertainment. However, if I feel the intuitive hit to play, we'll explore it. You must be: Making the world better, Creative and fun, Open to a higher power and intuitive guidance. Start with the Partnership Clarity Session first. If there's an aligned fit for deeper work, we'll see where it goes from there."
    },
    {
      question: "Can I skip the session and apply for consulting directly?",
      answer: "You can apply, but I highly recommend doing the session first. It's the best way for both of us to assess alignment before committing to deeper work."
    },
    {
      question: "Can you speak at or lead an experience at my event?",
      answer: "Yes! I love creating gamified networking experiences and leading intentional connection games. A few of my supra connector colleagues and I can create bespoke experiences for your event, or I can speak about ICK to STICK and lead transformational games solo. Options include: Gamified networking experiences, ICK to STICK keynote or workshop, Intentional connection games, and Bespoke transformation experiences. If you're interested, fill out the consulting application with details about your event."
    },
    {
      question: "What does \"This is not for you if you're not fun\" mean?",
      answer: "It means I work best with people who bring lightness, curiosity, and a sense of play. If everything feels heavy and transactional, we're probably not aligned, and that's okay."
    }
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
          Frequently Asked Questions
        </h2>
        
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <div key={index} className="px-8">
                <FAQItem question={faq.question} answer={faq.answer} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
